import {useCallback, useEffect, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {getDungeonById, searchDungeons} from "@/api/DungeonApi.ts";
import type {Dungeon} from "@/dungeons/model/Dungeon.ts";
import SearchPopover from "@/search/SearchPopover.tsx";
import {Button} from "@/components/ui/button.tsx";
import {DEFAULT_SIGHT_RADIUS_METERS} from "@/tabletop/visibility/SightRange.ts";
import type {DungeonSettings} from "@/encounter/model/DungeonSettings.ts";
import type {EncounterFormInput, EncounterFormOutput} from "./EncounterFormSchema.ts";

export default function EncounterDungeonForm({fixed}: {fixed: boolean}) {
    const {control, setValue, formState: {errors}} = useFormContext<EncounterFormInput, unknown, EncounterFormOutput>();
    const settings = useWatch({control, name: "dungeonSettings"}) as DungeonSettings | undefined;
    const [dungeon, setDungeon] = useState<Dungeon | null>(null);
    useEffect(() => {
        if (!settings?.dungeonId) return;
        let active = true;
        void getDungeonById(settings.dungeonId).then(result => {if (active) setDungeon(result);});
        return () => {active = false;};
    }, [settings?.dungeonId]);

    const search = useCallback((query: string, signal: AbortSignal) =>
        searchDungeons({page: 0, size: 20}, {name: query}, signal), []);
    const choose = (selected: Dungeon) => {
        const firstLevel = [...selected.levels].sort((a, b) => a.sortIndex - b.sortIndex)[0];
        if (!firstLevel) return;
        setDungeon(selected);
        setValue("dungeonSettings", {
            dungeonId: selected.id,
            levelId: firstLevel.id,
            sightRadiusMeters: DEFAULT_SIGHT_RADIUS_METERS,
            showGrid: settings?.showGrid ?? false,
            gridOpacity: settings?.gridOpacity ?? 0.5,
        }, {shouldDirty: true, shouldValidate: true});
    };
    return <section className="rounded-xl border border-border/80 bg-card/90 p-5 shadow-lg shadow-black/10">
        <h2 className="text-lg font-semibold">Dungeon</h2>
        <p className="mb-4 text-sm text-muted-foreground">{fixed
            ? "Dieser Encounter bleibt dauerhaft mit diesem Dungeon verbunden. Level und Sicht-Einstellungen bearbeitest du auf der Karte."
            : "Wähle einen Dungeon mit mindestens einem Level. Nach dem Erstellen kann er nicht mehr gewechselt werden."}</p>
        {fixed ? <div className="rounded-lg border bg-surface p-3 font-medium">{dungeon?.name ?? "Dungeon wird geladen…"}</div>
            : <>
                <SearchPopover<Dungeon> label="Dungeon auswählen" placeholder="Dungeon suchen…" minQueryLength={0}
                    search={search} getKey={item => item.id} onSelect={choose}
                    renderItem={({item, select}) => <Button type="button" variant="ghost" onClick={select}
                        disabled={item.levels.length === 0} className="w-full justify-start rounded-none">
                        {item.name}{item.levels.length === 0 && " (keine Level)"}
                    </Button>}/>
                {dungeon && <p className="mt-3 text-sm">Ausgewählt: <strong>{dungeon.name}</strong> · Startlevel: {dungeon.levels.find(level => level.id === settings?.levelId)?.name}</p>}
                {errors.dungeonSettings && <p role="alert" className="mt-2 text-sm text-destructive">Dungeon mit Level ist erforderlich.</p>}
            </>}
    </section>;
}
